import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import Loading from '@ingka/loading';
import Text from '@ingka/text';
import '@ingka/inline-message/dist/style.css';
import { useEffect, useState } from 'react';
import {
  createNotificationRecipient,
  listNotificationRecipients,
  type NotificationRecipient,
  updateNotificationRecipient,
} from '@/api/client';
import RecipientModal from '@/components/admin/RecipientModal';
import styles from './AdminCatalog.module.css';

type ModalState =
  | { open: false }
  | { open: true; mode: 'create' }
  | { open: true; mode: 'edit'; recipient: NotificationRecipient };

export default function AdminNotificationsPage() {
  const [items, setItems] = useState<NotificationRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ open: false });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { recipients } = await listNotificationRecipients();
      setItems(recipients);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is unmemoized and intended to run once on mount; adding it would re-fetch on every render.
  useEffect(() => {
    load();
  }, []);

  const handleSave = async (data: { email: string; name: string; active?: boolean }) => {
    if (modal.open && modal.mode === 'create') {
      await createNotificationRecipient({
        email: data.email,
        name: data.name || null,
      });
    } else if (modal.open && modal.mode === 'edit') {
      await updateNotificationRecipient(modal.recipient.id, {
        name: data.name || null,
        active: data.active,
      });
    }
    setModal({ open: false });
    await load();
  };

  return (
    <>
      <div className={styles.header}>
        <div>
          <Text tagName="h1" textStyle="Heading.L">
            Notification recipients
          </Text>
          <Text tagName="p" textStyle="Body.M" className={styles.subtitle}>
            People who receive an email whenever a user uploads a file.
          </Text>
        </div>
        <Button
          type="emphasised"
          text="Add recipient"
          onClick={() => setModal({ open: true, mode: 'create' })}
        />
      </div>

      {error && (
        <div className={styles.errorWrap}>
          <InlineMessage variant="negative" title="Error" body={error} />
        </div>
      )}

      {loading ? (
        <div className={styles.loadingWrap}>
          <Loading text="Loading recipients..." />
        </div>
      ) : (
        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead className={styles.thead}>
              <tr>
                <th className={styles.th}>Name</th>
                <th className={styles.th}>Email</th>
                <th className={styles.th}>Added by</th>
                <th className={styles.th}>Active</th>
                <th className={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className={styles.td} style={{ textAlign: 'center' }}>
                    <span className={styles.dimText}>
                      No recipients yet. Add one to start sending notifications.
                    </span>
                  </td>
                </tr>
              )}
              {items.map((r) => (
                <tr key={r.id} className={styles.tr}>
                  <td className={styles.td}>{r.name || <span className={styles.dimText}>—</span>}</td>
                  <td className={styles.td}>
                    <code className={styles.idCell}>{r.email}</code>
                  </td>
                  <td className={styles.td}>{r.added_by}</td>
                  <td className={styles.td}>
                    {r.active ? (
                      <span className={styles.activeOn}>●</span>
                    ) : (
                      <span className={styles.activeOff}>●</span>
                    )}
                  </td>
                  <td className={styles.tdRight}>
                    <Button
                      type="secondary"
                      size="small"
                      text="Edit"
                      onClick={() => setModal({ open: true, mode: 'edit', recipient: r })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal.open && (
        <RecipientModal
          title={modal.mode === 'create' ? 'Add recipient' : 'Edit recipient'}
          mode={modal.mode}
          initial={
            modal.mode === 'edit'
              ? {
                  email: modal.recipient.email,
                  name: modal.recipient.name ?? '',
                  active: modal.recipient.active,
                }
              : undefined
          }
          onClose={() => setModal({ open: false })}
          onSave={handleSave}
        />
      )}
    </>
  );
}